import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import useEscapeKey from 'components/common/UseEscapeKey/UseEscapeKey';
import UseToastComponent from 'components/common/UseToast';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Form, FormControl, Modal, ModalProps, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import { DEFAULT_SELECTED_VALUE, SELECTION_CONDITION } from '../constants';
import { FoodSystemMenu, SubMenuGroup } from '../models/submenulist';
import subMenuGroupService from '../subMenuService';
import { selectMenuOrderInGroups, selectSubMenuGroup, subMenuGroupActions } from '../subMenuSlice';
import DefaultSettingModal from './DefaultSettingModal';
import './SubMenuList.scss';

const DisplaySubMenuGroupDetail = forwardRef<
  ModalProps,
  {
    subMenuGroup?: {};
    onSubMenuGroupModalClose?: () => void;
    onUpdateSubmenuGroup?: (subMenuGroup: SubMenuGroup | undefined) => void;
  }
>((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showGlobalModal } = useGlobalModal();

  //selectors
  const menuOrders = useAppSelector(selectMenuOrderInGroups);
  const subMenuGroupSelector = useAppSelector(selectSubMenuGroup);
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [showModal, setShowModal] = useState(false);
  const [showChildModal, setShowChildModal] = useState(false);
  const [subMenuGroup, setSubMenuGroup] = useState<SubMenuGroup>();
  const [selectedFoodSystemMenu, setSelectedFoodSystemMenu] = useState<FoodSystemMenu>();

  // ref
  const displayDefaultSettingModalRef = useRef<ModalProps>(DefaultSettingModal);

  // use effect
  useEffect(() => {
    if (props.subMenuGroup !== undefined && Object.keys(props.subMenuGroup).length > 0) {
      setSubMenuGroup(props.subMenuGroup as SubMenuGroup);
    }
  }, [props.subMenuGroup]);

  useEffect(() => {
    if (subMenuGroupSelector !== undefined && Object.keys(subMenuGroupSelector).length > 0) {
      setSubMenuGroup(subMenuGroupSelector);
      setShowModal(true);
    } else {
      setSubMenuGroup(undefined);
    }
  }, [subMenuGroupSelector]);

  useImperativeHandle(ref, () => ({
    closeModal() {
      setShowModal(false);
    },
    openModal() {
      setShowModal(true);
    },
    // call from external parent page
    openModalExternal(subMenuGroupId: number, dataDate?: Date) {
      dispatch(subMenuGroupActions.fetchSubMenuGroupById({ subMenuGroupId, dataDate }));
    },
    onReset() {
      dispatch(subMenuGroupActions.removeStateSubMenuGroupById({} as SubMenuGroup));
    },
  }));

  const handleUpdateSubmenuGroup = () => {
    if (subMenuGroup) {
      const submenuGroupObj = {
        id: subMenuGroup.id,
        selection_condition: subMenuGroup.selection_condition ?? DEFAULT_SELECTED_VALUE,
        display_name_ja: subMenuGroup.display_name_ja,
        display_name_en: subMenuGroup.display_name_en,
        display_name_vi: subMenuGroup.display_name_vi,
        display_name_ko: subMenuGroup.display_name_ko,
        display_name_zh_hant: subMenuGroup.display_name_zh_hant,
        display_name_zh_hans: subMenuGroup.display_name_zh_hans,
        alias_name_ja: subMenuGroup.alias_name_ja,
        alias_name_en: subMenuGroup.alias_name_en,
        alias_name_vi: subMenuGroup.alias_name_vi,
        alias_name_ko: subMenuGroup.alias_name_ko,
        alias_name_zh_hant: subMenuGroup.alias_name_zh_hant,
        alias_name_zh_hans: subMenuGroup.alias_name_zh_hans,
        note: subMenuGroup.note,
        check_duplicate_submenu_flg: subMenuGroup.check_duplicate_submenu_flg,
      } as SubMenuGroup;
      (async () => {
        try {
          const response = await subMenuGroupService.patchSubMenuGroup(submenuGroupObj);
          showGlobalModal(() => (
            <AlertModal
              title={t('App.info')}
              message={response.message}
              onClose={() => {
                if (props.onUpdateSubmenuGroup) {
                  props.onUpdateSubmenuGroup(submenuGroupObj);
                }
                handleCloseModal();
              }}
            />
          ));
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  };

  const handleChangeSelectedCondition = (event: ChangeEvent<HTMLSelectElement>) => {
    if (subMenuGroup) {
      setSubMenuGroup({ ...subMenuGroup, selection_condition: parseInt(event.currentTarget.value) });
    }
  };

  const handleChangeCheckDuplicateSubMenuFlg = (event: ChangeEvent<HTMLInputElement>) => {
    if (subMenuGroup) {
      setSubMenuGroup({ ...subMenuGroup, check_duplicate_submenu_flg: event.currentTarget.checked });
    }
  };

  const handleChangeSubMenuGroupValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const property_name = event.currentTarget.name;
    const value = event.currentTarget.value;
    if (subMenuGroup) {
      const obj = {
        [property_name]: value,
      };
      setSubMenuGroup({ ...subMenuGroup, ...obj });
    }
  };

  //handle function
  const handleDisplayDefaultSelectionSettingsModal = () => {
    setShowChildModal(true);
    displayDefaultSettingModalRef.current.openModal();
  };

  const handleSetFoodSystemMenu = (foodSystemMenu: FoodSystemMenu | undefined) => {
    setSelectedFoodSystemMenu(foodSystemMenu);
  };

  const handleSetDefaultSettingFlag = (newSubMenuGroup: SubMenuGroup) => {
    if (subMenuGroup) {
      const obj = { ...subMenuGroup, default_setting_flg: newSubMenuGroup.default_setting_flg };
      setSubMenuGroup(obj);
      const submenuGroupObj = {
        id: subMenuGroup.id,
        default_setting_flg: newSubMenuGroup.default_setting_flg,
        selection_condition: subMenuGroup.selection_condition ?? DEFAULT_SELECTED_VALUE,
        display_name_ja: subMenuGroup.display_name_ja,
        display_name_en: subMenuGroup.display_name_en,
        display_name_vi: subMenuGroup.display_name_vi,
        display_name_ko: subMenuGroup.display_name_ko,
        display_name_zh_hant: subMenuGroup.display_name_zh_hant,
        display_name_zh_hans: subMenuGroup.display_name_zh_hans,
        alias_name_ja: subMenuGroup.alias_name_ja,
        alias_name_en: subMenuGroup.alias_name_en,
        alias_name_vi: subMenuGroup.alias_name_vi,
        alias_name_ko: subMenuGroup.alias_name_ko,
        alias_name_zh_hant: subMenuGroup.alias_name_zh_hant,
        alias_name_zh_hans: subMenuGroup.alias_name_zh_hans,
        note: subMenuGroup.note,
      } as SubMenuGroup;
      if (props.onUpdateSubmenuGroup) {
        props.onUpdateSubmenuGroup(submenuGroupObj);
      }
    }
  };

  const handleCloseModal = () => {
    if (props.onSubMenuGroupModalClose !== undefined) {
      props.onSubMenuGroupModalClose();
    }
    if (!showChildModal) {
      setShowModal(false);
      displayDefaultSettingModalRef.current.closeModal();
    }
  };

  function handleCloseDefaultSettingFlagModal() {
    setShowChildModal(false);
  }

  useEscapeKey(handleCloseModal);

  return (
    <>
      <UseToastComponent />
      <DefaultSettingModal
        ref={displayDefaultSettingModalRef}
        subMenuGroup={subMenuGroup}
        handleSetFoodSystemMenu={handleSetFoodSystemMenu}
        menuOrders={menuOrders}
        handleSetDefaultSettingFlag={handleSetDefaultSettingFlag}
        onClose={handleCloseDefaultSettingFlagModal}
      />
      <Modal
        dialogClassName="form-sub-menu-group"
        aria-labelledby="contained-modal-title-vcenter"
        size="lg"
        centered
        show={showModal}
        backdrop={'static'}
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <FeatureTitleBar title={t('SubMenuGroupListFeature.Text.submenu_group_details')} />
        </Modal.Header>
        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <div>
            <div>{t('SubMenuGroupListFeature.Text.guide1')}</div>
            <div className="ps-3">{t('SubMenuGroupListFeature.Text.guide2')}</div>
            <div className="ps-3">{t('SubMenuGroupListFeature.Text.guide3')}</div>
          </div>
          <div>
            <div className="d-flex justify-content-end mb-3">
              <button className="btn btn--dark-green" type="button" onClick={handleUpdateSubmenuGroup}>
                {t('SubMenuGroupListFeature.Component.save')}
              </button>
            </div>
            <Table className="table table-bordered text-center align-middle mb-3">
              <tbody>
                <tr>
                  <th colSpan={2} className="text-center" style={{ width: '40%' }}>
                    {t('SubMenuGroupListFeature.Text.submenugroup_name')}
                  </th>
                  <td>{subMenuGroup?.name || ''}</td>
                </tr>
                <tr>
                  <th colSpan={2}>{t('SubMenuGroupListFeature.Text.selection_condition')}</th>
                  <td>
                    <Form.Select
                      className="text-center"
                      onChange={handleChangeSelectedCondition}
                      defaultValue={subMenuGroup?.selection_condition ?? DEFAULT_SELECTED_VALUE}
                    >
                      {SELECTION_CONDITION.map((condition, index) => {
                        return (
                          <option key={index} value={condition.value}>
                            {condition.key}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </td>
                </tr>
                <tr>
                  <th colSpan={2}>{t('SubMenuGroupListFeature.Text.kosenum')}</th>
                  <td>
                    <div>
                      <span className="h4">{subMenuGroup?.kosenum}</span>
                    </div>
                    <div className="d-flex justify-content-end fw-bold">
                      <Form.Check
                        style={{ maxWidth: '32%' }}
                        type="checkbox"
                        id={`check-duplicate-submenu-flg`}
                        label={t('SubMenuGroupListFeature.Text.check_duplicate_submenu_flg_title')}
                        defaultChecked={subMenuGroup?.check_duplicate_submenu_flg || false}
                        onChange={handleChangeCheckDuplicateSubMenuFlg}
                        disabled={(subMenuGroup?.kosenum || 1) < 2}
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <th rowSpan={6}>{t('SubMenuGroupListFeature.Text.display_name')}</th>
                  <th>{t('SubMenuGroupListFeature.Text.japanese')}</th>
                  <td>
                    <FormControl
                      name="display_name_ja"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.display_name_ja ? subMenuGroup.display_name_ja : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.english')}</th>
                  <td>
                    <FormControl
                      name="display_name_en"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.display_name_en ? subMenuGroup.display_name_en : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.simplified_chinese')}</th>
                  <td>
                    <FormControl
                      name="display_name_zh_hans"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.display_name_zh_hans ? subMenuGroup?.display_name_zh_hans : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.traditional_chinese')}</th>
                  <td>
                    <FormControl
                      name="display_name_zh_hant"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.display_name_zh_hant ? subMenuGroup.display_name_zh_hant : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.korean')}</th>
                  <td>
                    <FormControl
                      name="display_name_ko"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.display_name_ko ? subMenuGroup.display_name_ko : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.vietnamese')}</th>
                  <td>
                    <FormControl
                      name="display_name_vi"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.display_name_vi ? subMenuGroup.display_name_vi : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th rowSpan={6}>{t('SubMenuGroupListFeature.Text.alias_name')}</th>
                  <th>{t('SubMenuGroupListFeature.Text.japanese')}</th>
                  <td>
                    <FormControl
                      name="alias_name_ja"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.alias_name_ja ? subMenuGroup.alias_name_ja : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.english')}</th>
                  <td>
                    <FormControl
                      name="alias_name_en"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.alias_name_en ? subMenuGroup.alias_name_en : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.simplified_chinese')}</th>
                  <td>
                    <FormControl
                      name="alias_name_zh_hans"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.alias_name_zh_hans ? subMenuGroup?.alias_name_zh_hans : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.traditional_chinese')}</th>
                  <td>
                    <FormControl
                      name="alias_name_zh_hant"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.alias_name_zh_hant ? subMenuGroup.alias_name_zh_hant : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.korean')}</th>
                  <td>
                    <FormControl
                      name="alias_name_ko"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.alias_name_ko ? subMenuGroup.alias_name_ko : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th>{t('SubMenuGroupListFeature.Text.vietnamese')}</th>
                  <td>
                    <FormControl
                      name="alias_name_vi"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.alias_name_vi ? subMenuGroup.alias_name_vi : ''}
                    ></FormControl>
                  </td>
                </tr>
                <tr>
                  <th colSpan={2}>{t('SubMenuGroupListFeature.Text.default_selection')}</th>
                  <td>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-fill text-center">
                        <span>{selectedFoodSystemMenu ? selectedFoodSystemMenu.name : ''}</span>
                      </div>
                      <div>
                        <button
                          className="btn btn-primary"
                          disabled={subMenuGroup && subMenuGroup.sub_menus.length > 0 ? false : true}
                          type="button"
                          onClick={handleDisplayDefaultSelectionSettingsModal}
                        >
                          {t('SubMenuGroupListFeature.Component.setting')}
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th colSpan={2}>{t('SubMenuGroupListFeature.Table.remarks')}</th>
                  <td>
                    <FormControl
                      as="textarea"
                      rows={5}
                      name="note"
                      onChange={handleChangeSubMenuGroupValue}
                      defaultValue={subMenuGroup?.note ? subMenuGroup.note : ''}
                    ></FormControl>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
});

export default DisplaySubMenuGroupDetail;
